import { FlowType } from './proofreading.model';

/**
 * Statut du PDF Epson porté par `proofreading.flags.epson`.
 * Remplace le nœud kiosque « Vérifier Fichier Epson » du flux Diadem_Pdf_Epson :
 * le contrôle opérateur se fait maintenant dans LiveCo.
 */
export enum EpsonStatus {
    /** Le flux CloudFlow génère le PDF HD. */
    GENERATING = 'generating',
    /** PDF généré, en attente de vérification par l'opérateur. */
    TO_CHECK = 'to-check',
    /** Opérateur OK, fichier envoyé au hotfolder d'impression. */
    PRINTED = 'printed',
    /** Opérateur KO (ancien bouton « Pdf non conforme »). */
    REJECTED = 'rejected',
    /** Le flux CloudFlow est tombé en erreur. */
    ERROR = 'error'
}

export interface IEpsonFlag {
    status: EpsonStatus;
    /** Demande `epson-requests` dont ce flag est le reflet. */
    request_id?: string;
    file_url?: string;
    file_name?: string;
    hotfolder?: string;
    hotfolder_name?: string;
    profile?: string;
    /** Pages imprimées : « 1-3,5 », vide pour toutes (nombre sur les premières demandes). */
    pages?: string | number;
    with_cartouche?: boolean;
    warning?: string;
    from_delivery?: boolean;
    identifier?: string;
    flow?: FlowType;
    generated_at?: string;
    checked_at?: string;
    checked_by?: string;
    error?: string;
}

/** Destination d'impression : clé envoyée au serveur, libellé affiché. */
export interface IEpsonProfile {
    key: string;
    label: string;
}

/**
 * Destinations proposées au lancement, avec les libellés et l'ordre du menu
 * « Destination » du kiosque CloudFlow. Le serveur résout la clé en hotfolder
 * (voir EPSON_PROFILES dans liveco-server).
 */
export const EPSON_PROFILES: IEpsonProfile[] = [
    { key: 'epson7500-fogra39', label: 'Epson 7500 - ISO FOGRA39' },
    { key: 'epson7900-gmg2', label: 'Epson 7900 - ISO FOGRA39 GMG 2' },
    { key: 'epson7900-unidir', label: 'Epson 7900 - STANDARD UNIDIR' }
];
/**
 * Destination présélectionnée : la 7500. Le serveur garde la 7900 GMG 2 (défaut du
 * kiosque) pour les anciennes demandes enregistrées sans destination.
 */
export const EPSON_PROFILE_DEFAULT = 'epson7500-fogra39';

/**
 * Pages à imprimer, au format des boîtes d'impression : « 1-3, 5 ».
 * Chaîne vide : toutes les pages. Par défaut, la première seule, comme le kiosque.
 */
export const EPSON_PAGES_ALL = '';
export const EPSON_PAGES_DEFAULT = '1';

/**
 * Pages d'une sélection (à partir de 1), `null` pour toutes, ou message d'erreur.
 * Même règle que le serveur, pour signaler la saisie invalide sans attendre.
 */
export function parseEpsonPages(input: string, numberOfPages?: number): { pages: number[] | null; error?: string } {
    const text = (input || '').trim().toLowerCase();
    if (!text || ['toutes', 'tout', 'all', '*'].includes(text)) {
        return { pages: null };
    }
    const pages = new Set<number>();
    for (const token of text.replace(/\s*-\s*/g, '-').split(/[\s,;]+/).filter(Boolean)) {
        const match = token.match(/^(\d+)(?:-(\d+))?$/);
        if (!match) {
            return { pages: null, error: token };
        }
        const from = Number(match[1]);
        const to = match[2] !== undefined ? Number(match[2]) : from;
        if (from < 1 || to < from) {
            return { pages: null, error: token };
        }
        for (let p = from; p <= to && p <= 9999; p++) {
            pages.add(p);
        }
    }
    const sorted = [...pages].sort((a, b) => a - b);
    const missing = numberOfPages ? sorted.filter(p => p > numberOfPages) : [];
    if (missing.length) {
        return { pages: sorted, error: `> ${numberOfPages}` };
    }
    return { pages: sorted };
}

/** Forme compacte d'une sélection : [1, 2, 3, 5] -> « 1-3, 5 ». */
export function formatEpsonPages(pages: number[]): string {
    const sorted = [...new Set(pages)].sort((a, b) => a - b);
    const parts: string[] = [];
    for (let i = 0; i < sorted.length; i++) {
        let j = i;
        while (j + 1 < sorted.length && sorted[j + 1] === sorted[j] + 1) {
            j++;
        }
        parts.push(i === j ? `${sorted[i]}` : `${sorted[i]}-${sorted[j]}`);
        i = j;
    }
    return parts.join(', ');
}

/** Libellé d'une sélection enregistrée (anciennes demandes : nombre de premières pages). */
export function formatEpsonPagesLabel(pages: string | number | undefined, all: string): string {
    if (pages === undefined || pages === null || pages === '' || pages === 0) {
        return all;
    }
    if (typeof pages === 'number') {
        return pages === 1 ? '1' : `1-${pages}`;
    }
    return pages.replace(/,/g, ', ');
}

/** Case « Cartouche » du kiosque, cochée par défaut. */
export const EPSON_WITH_CARTOUCHE_DEFAULT = true;

/**
 * Ouverture des modales qui affichent un PDF Epson : hauteur fixe pour que le PDF
 * prenne la place restante sans faire défiler la fenêtre, et focus sur la fenêtre
 * plutôt que sur la croix de fermeture.
 */
export const EPSON_PDF_DIALOG_CONFIG = { width: '60vw', height: '85vh', autoFocus: 'dialog' } as const;

/** Réglages choisis au lancement d'une épreuve. */
export interface IEpsonPrintOptions {
    profile: string;
    /** « 1-3, 5 », vide pour toutes. */
    pages: string;
    withCartouche: boolean;
}

export enum EpsonAction {
    PRINT = 'print',
    VALIDATE = 'validate',
    REJECT = 'reject'
}

/** Origine d'une demande d'impression. */
export enum EpsonSource {
    PROOFREADING = 'proofreading',
    FILE = 'file'
}

/** Une demande d'impression Epson (historique, service `epson-requests`). */
export interface IEpsonPrintRequest extends IEpsonFlag {
    _id: string;
    source: EpsonSource;
    identifier: string;
    proofreading_id?: string;
    source_file: string;
    requested_at: string;
    requested_by_name?: string;
    checked_by_name?: string;
}

/** Relecture dont on peut tirer une Epson (ex-kiosque « PDF EPSON sur Dossier »). */
export interface IEpsonProofreadingOption {
    _id: string;
    flow: string;
    relecture?: number;
    /** Nom du PDF HD cartouché de la relecture. */
    file_name: string;
    /** État de la dernière Epson de la relecture, s'il y en a une. */
    epson_status?: EpsonStatus;
}

/** Contenu d'un dossier de projet, pour choisir le fichier à imprimer. */
export interface IEpsonFolderListing {
    identifier: string;
    root: string;
    folder: string;
    parent?: string;
    folders: { name: string; url: string }[];
    files: { name: string; url: string }[];
}

/** Réglages d'une épreuve lancée depuis le menu « Gérer ». */
export interface IEpsonFilePrintOptions extends IEpsonPrintOptions {
    identifier: string;
    file: string;
    flow: string;
}

/** Projet proposé par la recherche de la page Epson. */
export interface IEpsonProjectOption {
    identifier: string;
    description?: string;
    marque?: string;
}
